import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';

import { EducationalMaterialsUserGuard, CommunityManagerUserGuard, VerifiedUserGuard, isAdminUser } from '@trp/community/user/feature-user';
import {
  DEPLOYMENT_CHECK_UI_CONFIG,
  DeploymentCheckUiConfig,
} from '@trp/deployment-check/ui-config';
import { environment } from '../environments/environment';

export const routes: Routes = [
  {
    path: 'about',
    providers: [
      {
        provide: DEPLOYMENT_CHECK_UI_CONFIG,
        useValue: {
          buildInfo: environment.buildInfo,
          apiPath: environment.apiUrl + '/deploymentCheck',
        } as DeploymentCheckUiConfig,
      },
    ],
    loadChildren: () =>
      import('@trp/deployment-check/ui').then((m) => m.DeploymentCheckUiModule),
  },
  {
    path: 'notifications',
    loadChildren: () =>
      import('@trp/community/notification/feature-shell').then(
        (m) => m.CommunityNotificationFeatureShellModule
      ),
  },
  {
    path: 'home',
    loadChildren: () =>
      import('@trp/community/landing/feat-landing').then(
        (m) => m.CommunityLandingFeatLandingModule
      ),
  },
  {
    path: 'recycleon-oregon',
    redirectTo: 'home/recycleon-oregon',
    pathMatch: 'full',
  },
  {
    path: 'recycleon-colorado',
    redirectTo: 'home/recycleon-colorado',
    pathMatch: 'full',
  },
  {
    path: 'recommendation',
    canActivate: [VerifiedUserGuard],
    loadChildren: () =>
      import('@trp/community/coach/feature-shell').then(
        (m) => m.CommunityCoachFeatureShellModule
      ),
  },
  {
    path: 'drop-off-locations',
    canActivate: [VerifiedUserGuard, CommunityManagerUserGuard],
    loadChildren: () =>
      import('@trp/community/drop-off-location/feature-shell').then(
        (m) => m.DropOffLocationFeatureShellModule
      ),
  },
  {
    path: 'program-metrics',
    loadChildren: () =>
      import('@trp/community/program-metrics/feature-shell').then(
        (m) => m.ProgramMetricsFeatureShellModule
      ),
  },
  {
    path: 'map',
    loadChildren: () =>
      import('@trp/community/search/feature-shell').then(
        (m) => m.CommunitySearchFeatureShellModule
      ),
  },
  {
    path: 'resource',
    loadChildren: () =>
      import('@trp/community/resource/feature-shell').then(
        (d) => d.CommunityResourceFeatureShellModule
      ),
  },
  {
    path: 'admin',
    loadChildren: () =>
      import('@trp/community/admin/feature-shell').then(
        (d) => d.CommunityAdminFeatureShellModule
      ),
  },
  {
    // Replaces the temporary `admin/email-test` route: the test triggers are now
    // a section of the Automated Emails page, above the Email Logs table.
    path: 'admin/automated-emails',
    canActivate: [isAdminUser],
    loadComponent: () =>
      import('@trp/community/admin/feature-shell').then(
        (m) => m.AutomatedEmailsComponent
      ),
  },
  {
    path: 'access/your-requests',
    loadComponent: () =>
      import('@trp/community/access/feature-shell').then(
        (c) => c.YourRequestsComponent
      ),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('@trp/community/shared/routed-ui').then(
        (c) => c.ForbiddenComponent
      ),
    data: {
      message: 'forbidden.feature',
    },
  },
  {
    path: 'not-found',
    loadComponent: () =>
      import('@trp/community/shared/routed-ui').then(
        (c) => c.NotFoundComponent
      ),
  },
  {
    path: 'faq',
    canActivate: [VerifiedUserGuard],
    loadChildren: () =>
      import('@trp/community/faq/feature-shell').then(
        (m) => m.FaqFeatureShellModule
      ),
  },
  {
    path: 'glossary',
    loadChildren: () =>
      import('@trp/community/glossary/feature-shell').then(
        (m) => m.CommunityGlossaryFeatureShellModule
      ),
  },
  {
    path: 'contact-us',
    loadChildren: () =>
      import('@trp/community/contact-us/feature-shell').then(
        (m) => m.ContactUsFeatureShellModule
      ),
  },
  {
    path: 'educational-materials',
    // canActivate: [CAAUserGuard],
    canActivate: [EducationalMaterialsUserGuard],
    loadChildren: () =>
      import('@trp/chili-grafx/feature-shell').then(
        (m) => m.ChiliGrafxFeatureShellModule
      ),
  },  
  {
    path: 'webhook/community-updated/:id',
    canActivate: [
      async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        try {
          const id = route.paramMap.get('id');
          if (id) {
            const response = await fetch(
              `${environment.apiUrl}/webhook/community-updated/${id}`,
              {
                headers: {
                  'webhook-api-key': 'fake-api-key',
                },
              }
            );
            if (response.status !== 200) {
              throw Error('Not a valid url');
            }
            window.location.href = '/home';
          }
          return false;
        } catch (error) {
          console.error(error);
          window.location.href = '/not-found';
          return true;
        }
      },
    ],
    loadComponent: () =>
      import('@trp/community/shared/routed-ui').then(
        (c) => c.NotFoundComponent
      ),
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
];
